import { Api } from "services/api/api.service";
import { ScheduleDispatch } from "./scheduleStore";
import {
  SchedulePreferences,
  scheduleInitState,
} from "./scheduleStore.reducer";
import { ScheduleShiftItem } from "web-apps/company/components/schedule/types/schedule.types";
import { ScheduleSortBy } from "services/api/company/schedule.service";
import { err, isOk, ok } from "utils/result";
import type { Result } from "utils/result";

let requestCounter = 0;

const schedulePreferencesStorageKey = (companyId: number): string =>
  `PERSISTED::SchedulePreferences::${companyId}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isGroupByOption = (
  value: unknown,
): value is SchedulePreferences["groupByOption"] =>
  value === "person" || value === "team";

const isIntervalOption = (
  value: unknown,
): value is SchedulePreferences["intervalOption"] =>
  value === "weekly" || value === "monthly";

const isSizeOption = (
  value: unknown,
): value is SchedulePreferences["sizeOption"] =>
  value === "large" || value === "small";

const parseSchedulePreferences = (
  raw: string,
): Result<SchedulePreferences, "invalid-preferences"> => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return err("invalid-preferences");

    const groupByOption = parsed.groupByOption;
    const intervalOption = parsed.intervalOption;
    const sizeOption = parsed.sizeOption;

    if (
      !isGroupByOption(groupByOption) ||
      !isIntervalOption(intervalOption) ||
      !isSizeOption(sizeOption)
    ) {
      return err("invalid-preferences");
    }

    return ok({ groupByOption, intervalOption, sizeOption });
  } catch {
    return err("invalid-preferences");
  }
};

const getSchedule =
  (dispatch: ScheduleDispatch) =>
  async (
    companyId: number,
    fromDate: string,
    toDate: string,
    page: number,
    pageSize: number,
    sortBy?: ScheduleSortBy,
    workGroupIds?: number[],
    workerIds?: number[],
  ) => {
    const requestId = (requestCounter += 1);
    dispatch({ type: "SET_ACTIVE_REQUEST_ID", payload: requestId });
    dispatch({
      type: "SET_IS_LOADING",
      payload: { value: page === 0, requestId },
    });
    dispatch({
      type: "SET_IS_LOADING_MORE",
      payload: { value: page > 0, requestId },
    });
    dispatch({ type: "SET_REQUEST_ERROR", payload: null });

    await Api()
      .company.schedule.getSchedule(
        companyId,
        fromDate,
        toDate,
        page,
        pageSize,
        sortBy,
        workGroupIds,
        workerIds,
      )
      .then((res) => {
        dispatch({
          type: "SET_SCHEDULE",
          payload: {
            data: res.data,
            requestId,
            append: page > 0,
            page,
            pageSize,
          },
        });
      })
      .catch((err: Error) => {
        console.error("Error fetching schedule", err);
        dispatch({
          type: "SET_REQUEST_ERROR",
          payload: { message: "", err },
        });
      });

    dispatch({ type: "SET_IS_LOADING", payload: { value: false, requestId } });
    dispatch({
      type: "SET_IS_LOADING_MORE",
      payload: { value: false, requestId },
    });
  };

const setSchedulePreferences =
  (dispatch: ScheduleDispatch) =>
  (preferences: SchedulePreferences): void => {
    dispatch({ type: "SET_PREFERENCES", payload: preferences });
  };

const loadSchedulePreferences =
  (dispatch: ScheduleDispatch) =>
  (companyId: number): void => {
    const raw = localStorage.getItem(schedulePreferencesStorageKey(companyId));
    if (!raw) return;
    const parsed = parseSchedulePreferences(raw);
    if (!isOk(parsed)) {
      const fallback = scheduleInitState.preferences;
      dispatch({ type: "SET_PREFERENCES", payload: fallback });
      try {
        localStorage.setItem(
          schedulePreferencesStorageKey(companyId),
          JSON.stringify(fallback),
        );
      } catch {
        localStorage.removeItem(schedulePreferencesStorageKey(companyId));
      }
      return;
    }
    dispatch({ type: "SET_PREFERENCES", payload: parsed.value });
  };

const persistSchedulePreferences =
  (_dispatch: ScheduleDispatch) =>
  (companyId: number, preferences: SchedulePreferences): void => {
    try {
      localStorage.setItem(
        schedulePreferencesStorageKey(companyId),
        JSON.stringify(preferences),
      );
    } catch {
      return;
    }
  };

export const scheduleActions = (dispatch: ScheduleDispatch) => ({
  getSchedule: getSchedule(dispatch),
  resetSchedule: () => dispatch({ type: "RESET_SCHEDULE" }),
  updateShift: (gigShiftId: number, patch: Partial<ScheduleShiftItem>) =>
    dispatch({ type: "UPDATE_SHIFT", payload: { gigShiftId, patch } }),
  setSchedulePreferences: setSchedulePreferences(dispatch),
  loadSchedulePreferences: loadSchedulePreferences(dispatch),
  persistSchedulePreferences: persistSchedulePreferences(dispatch),
});
